import { toast } from "bulma-toast";
import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover } from "react-tiny-popover";
import { Modal } from "../../../../../components";
import { useStore } from "../../../../../stores";

type Skill = {
  value: string;
  title: string;
};

export default function BotmateAIPopover({
  setIsLoading,
  message,
  setMessage,
  position = "composer",
  isResponsive = false,
}: any) {
  const [isAIPopoverOpen, setIsAIPopoverOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { botmateSkill, selectedChat, setLabels } = useStore("chats");
  const { addLabel } = useStore("bots");
  const { t } = useTranslation();
  const [botmateSkillModal, setBotmateSkillModal] = useState<{
    open: boolean;
    title: string;
    skill: string;
    content: string;
  } | null>({
    open: false,
    title: "",
    skill: "",
    content: "",
  });

  const skills: Skill[] = [
    {
      value: "reply-suggestions",
      title: "suggestAReplyTitle",
    },
    {
      value: "text-summarization",
      title: "textSummarizationTitle",
    },
    {
      value: "tone-and-grammar-review",
      title: "toneAndGrammarReviewTitle",
    },
    {
      value: "conversation-insights",
      title: "conversationInsightsTitle",
    },
    {
      value: "sales-intelligence",
      title: "salesIntelligenceTitle",
    },
    {
      value: "sentiment-analysis",
      title: "sentimentAnalysisTitle",
    },
    {
      value: "sale-closing-tips",
      title: "saleClosingTipsTitle",
    },
  ];

  const loadBotmateSkill = (skill: Skill) => {
    setIsAIPopoverOpen(false);
    setLoading(true);
    setIsLoading(true);

    botmateSkill(skill.value, message)
      .then((value: any) => {
        if (!value) return null;

        if (skill.value === "reply-suggestions") {
          setMessage(value);
        } else {
          setBotmateSkillModal({
            open: true,
            title: t(skill.title.replace("Title", "")),
            skill: skill.value,
            content: value,
          });
        }
      })
      .finally(() => {
        setLoading(false);
        setIsLoading(false);
      });
  };

  const createLabel = async (name: string) => {
    try {
      const label = await addLabel(name.trim() || "", "#2A79EF");

      if (!label || !selectedChat) {
        return;
      }

      if (selectedChat.labels?.includes(label)) {
        return;
      }

      await setLabels(label, "add");

      toast({
        message: "Label added to the chat successfully",
        position: "top-right",
        dismissible: true,
        type: "is-success",
        pauseOnHover: true,
      });

      setBotmateSkillModal(null);
    } catch (error) {
      if (error instanceof Error) {
        toast({
          message: error.message,
          position: "top-right",
          dismissible: true,
          type: "is-warning",
          pauseOnHover: true,
        });
      }
    }
  };

  return (
    <>
      <Popover
        isOpen={isAIPopoverOpen}
        positions={
          position === "composer" ? ["top", "right"] : ["bottom", "left"]
        }
        align={position === "composer" ? "start" : "end"}
        containerStyle={{ zIndex: "100000000" }}
        padding={7}
        onClickOutside={() => setIsAIPopoverOpen(false)}
        content={() => (
          <div className="has-border has-background-white has-rounded-corners">
            {skills
              .filter((f) => {
                if (
                  position === "header" &&
                  ["reply-suggestions", "tone-and-grammar-review"].includes(
                    f.value
                  )
                ) {
                  return false;
                }
                return true;
              })
              .map((skill) => (
                <div
                  key={skill.title}
                  onClick={() => loadBotmateSkill(skill)}
                  className="is-clickable has-border-top py-2 px-5"
                >
                  <span className="is-6 mb-0 subtitle">{t(skill.title)}</span>
                </div>
              ))}
          </div>
        )}
      >
        <button
          className={clsx("button has-text-primary", {
            "is-loading": loading,
            "is-ghost is-no-box-shadow": position === "composer",
            "is-rounded": position === "header" && !isResponsive,
            "is-ghost no-focus": position === "header" && isResponsive,
          })}
          onClick={() => setIsAIPopoverOpen(true)}
        >
          {!loading && (
            <span style={{ position: "absolute" }} className="icon">
              <img src="/assets/magic-wand.svg" alt="" />
            </span>
          )}
        </button>
      </Popover>
      <Modal
        isOpen={botmateSkillModal?.open || false}
        isMediumScreen
        title={botmateSkillModal?.title || ""}
        onClose={() => setBotmateSkillModal(null)}
        onPrimary={() => {
          if (botmateSkillModal?.skill === "tone-and-grammar-review") {
            setMessage(botmateSkillModal?.content);
            setBotmateSkillModal(null);
          }
        }}
        primaryText={
          botmateSkillModal?.skill === "tone-and-grammar-review"
            ? "Use This"
            : undefined
        }
      >
        {botmateSkillModal?.content && (
          <pre className="custom-pre">
            {botmateSkillModal?.skill === "sales-intelligence"
              ? JSON.parse(botmateSkillModal.content)?.text
              : botmateSkillModal.content}
          </pre>
        )}

        {botmateSkillModal?.skill === "sales-intelligence" && (
          <div className="mt-4">
            {JSON.parse(botmateSkillModal.content)?.labels?.map(
              (label: string) => (
                <span
                  onClick={() => createLabel(label)}
                  key={label}
                  className="tag is-primary is-medium mr-3 my-2 is-clickable"
                >
                  {label}
                </span>
              )
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
